// The glyph table and the manifest renderer, split out of `Icon.tsx`.
//
// `eslint --max-warnings=0` enforces `react-refresh/only-export-components`,
// and the rule is right: a file that exports both a component and a plain
// function makes Vite's fast-refresh fall back to a full reload, so editing an
// icon costs a state reset. The first version of `Icon.tsx` exported
// `ICON_NAMES`, `NAV_ICON` and `renderModuleIcon` alongside the component and
// tripped it three times.
//
// The table is also the right place for it on the merits: `GLYPHS` is *data*,
// and a `.tsx` file whose main export is a component is a better place to look
// for a component.

/**
 * Path data per icon, as `<path>` / `<rect>` / `<circle>` children.
 * Keys are the 13 names fixed by spec §1 E4 — no more, no fewer, so a stray
 * `Sparkles` import from lucide cannot creep back in.
 *
 * Geometry is lifted verbatim from `mockup/w41.0-*.html`: the 14 acceptance
 * images are this sprint's top authority, so redrawing the glyphs would produce a
 * set that *disagrees with the thing being approved*.
 */
export const GLYPHS = {  home: (
    <>
      <path d="M4 11 12 4l8 7" />
      <path d="M6 10.2V20h12v-9.8" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M8 3v4M16 3v4M3 11h18" />
    </>
  ),
  book: (
    <>
      <path d="M4 5.5A2 2 0 0 1 6 3.5h5v17H6a2 2 0 0 1-2-2z" />
      <path d="M20 5.5a2 2 0 0 0-2-2h-5v17h5a2 2 0 0 0 2-2z" />
    </>
  ),
  star: <path d="m12 3.2 2.7 5.7 6.3.8-4.6 4.4 1.2 6.2L12 17.4l-5.6 2.9 1.2-6.2L3 9.7l6.3-.8z" />,
  more: (
    <>
      {/* Filled, not stroked: a 1.9px stroke on a 1.6px circle renders as a
          filled dot anyway, and drawing it as strokes costs three subpaths to
          say the same thing. */}
      <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  plus: <path d="M12 6v12M6 12h12" />,
  heart: <path d="M12 20.4C7.2 16.5 3.4 13 3.4 9.4A4.4 4.4 0 0 1 12 7a4.4 4.4 0 0 1 8.6 2.4c0 3.6-3.8 7.1-8.6 11z" />,
  bell: (
    <>
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  pin: (
    <>
      <path d="M12 20h9" />
      <path d="M16.4 3.6a2 2 0 0 1 2.8 2.8L7.5 18 3 19.5 4.5 15z" />
    </>
  ),
  chevron: <path d="m9 5 7 7-7 7" />,
  trash: (
    <>
      <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  spark: <path d="M12 2.5 13.9 9l6.6 1.9-6.6 1.9L12 19.4l-1.9-6.6L3.5 11 10.1 9z" />,
} as const satisfies Record<string, React.ReactNode>;

/**
 * The 13 names, **derived from the table** rather than declared beside it — a
 * hand-written union can drift from the table, and that drift is silent: an
 * icon added to `GLYPHS` without a matching union member still type-checks at
 * the table and fails only at the call site.
 */
export type IconName = keyof typeof GLYPHS;

/**
 * The 13 names, as a runtime array. Exists so a guard test can assert the set is
 * exactly these — the failure this prevents is a *silent* one: an icon added to
 * GLYPHS because a screen needed it, with nobody noticing the set grew.
 */
export const ICON_NAMES = Object.keys(GLYPHS) as IconName[];

